import { Alert, Box, CircularProgress, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, Switch, TextField, Typography } from "@mui/material";
import { IFacturaMetodoPago } from "./FacturarCitaDialog";
import IServicio from "../../../types/Servicio";
import ConfigUtils from "../../../utils/Config";
import * as constantes from "../../../common/Constantes";
import { IFacturaErrors, getCurrencySymbol, formatNumeroFactura } from "../../../utils/Factura";
import IAlert from "../../../types/Alert";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { useEffect, useState } from "react";
import { IBonoPaciente } from "../../../types/BonoPaciente";
import PacienteService from "../../../services/PacienteService";
import { useNavigate } from "react-router-dom";
import ILog from "../../../types/Log";
import LogService from "../../../services/LogService";

interface FacturarFormProps {
  pacienteId: string;
  moneda: string;

  tipoPago: "total" | "parcial";
  setTipoPago: (value: "total" | "parcial") => void;

  selectedServicio: IServicio | undefined;
  setSelectedServicio: (value: IServicio | undefined) => void;

  facturable: boolean;
  setFacturable: (value: boolean) => void;

  motivoNoFacturable: string;
  setMotivoNoFacturable: (value: string) => void;

  servicio: string;
  setServicio: (value: string) => void;

  concepto: string;
  setConcepto: (value: string) => void;

  precio: number;
  setPrecio: (value: number) => void;

  impuesto: number;
  setImpuesto: (value: number) => void;

  precioTotal: number;
  setPrecioTotal: (value: number) => void;

  inputPrecioBase: string;
  setInputPrecioBase: (value: string) => void;

  errors: any;
  setErrors: (val: any) => void;

  confirmNoFacturable: boolean;
  setConfirmNoFacturable: (value: boolean) => void;

  facturaPagada: boolean;
  setFacturaPagada: (value: boolean) => void;

  metodoPago: IFacturaMetodoPago | undefined;
  setMetodoPago: (value: IFacturaMetodoPago | undefined) => void;

  bonoId: string;
  setBonoId: (value: string) => void;
  bonoIdOriginal: string;

  isEdit: boolean;
  isRectificar: boolean;
  numeroFactura: string;
  fechaFactura: string;
  tieneComplementaria: boolean;

  setAlert: (alert: IAlert) => void;

  esRectificativa: boolean;

  onBonoSeleccionadoChange?: (bono: IBonoPaciente | null) => void;

  // Entidad ya fijada de la factura (edición/rectificación) — si viene informada, solo se
  // pueden elegir bonos de esa misma entidad. undefined = creación nueva, sin restricción (la
  // entidad la determina el propio bono elegido).
  facturaEntidadId?: string | null;
}

const FacturarForm = ({
  pacienteId,
  moneda,
  tipoPago, setTipoPago,
  selectedServicio, setSelectedServicio,
  facturable, setFacturable,
  motivoNoFacturable, setMotivoNoFacturable,
  servicio, setServicio,
  concepto, setConcepto,
  precio, setPrecio,
  impuesto, setImpuesto,
  precioTotal, setPrecioTotal,
  inputPrecioBase, setInputPrecioBase,
  errors, setErrors,
  setConfirmNoFacturable,
  facturaPagada, setFacturaPagada,
  metodoPago, setMetodoPago,
  bonoId, setBonoId, bonoIdOriginal,
  isEdit,
  isRectificar,
  numeroFactura, 
  fechaFactura,
  tieneComplementaria,
  setAlert,
  esRectificativa,
  onBonoSeleccionadoChange,
  facturaEntidadId
}: FacturarFormProps) => {
    const history = useNavigate();
    const [bonos, setBonos] = useState<IBonoPaciente[]>([]);
    const [loadingBonos, setLoadingBonos] = useState(false);
    const [bonoSeleccionado, setBonoSeleccionado] = useState<IBonoPaciente | null>(null);
    const [selectedMetodo, setSelectedMetodo] = useState(bonoId || metodoPago?.nombre || "");

    const servicios = ConfigUtils.getServicios();
    const metodosPago = ConfigUtils.getMetodosPago();
    const simboloMoneda = getCurrencySymbol(moneda);

    useEffect(() => {
      if (!bonoSeleccionado) return;
    
      const nombresServicios = bonoSeleccionado.servicios
        .map(id =>
          servicios.find(s => parseInt(s.internalID!) === id)?.nombre
        )
        .filter(Boolean);
      
      const servicioIncluido = nombresServicios.includes(servicio);
      const validoParaFactura =
        tipoPago === "total" && servicioIncluido;
      
      if (!validoParaFactura && bonoSeleccionado._id !== bonoId) {
        setBonoSeleccionado(null);
        setBonoId("");
        setMetodoPago(undefined);
        setSelectedMetodo("");
      }
    }, [servicio, tipoPago]);

    useEffect(() => {
      if (!pacienteId) return;
    
      cargarBonos();
    }, [pacienteId, isEdit, isRectificar]);

    useEffect(() => {
      if (!bonos.length) return;
      if (!bonoId) return;

      const bonoUsado = bonos.find(b => b._id === bonoId);
      if (!bonoUsado) return;

      setBonoSeleccionado(bonoUsado);
      setSelectedMetodo(bonoUsado._id!);
    }, [bonos, bonoId]);

    // El pago con bono hereda la entidad de facturación de la factura de compra del bono —
    // se avisa al padre para que bloquee el selector de entidad mientras esté seleccionado.
    useEffect(() => {
      onBonoSeleccionadoChange?.(bonoSeleccionado);
    }, [bonoSeleccionado]);

    const cargarBonos = async () => {
      try {
        setLoadingBonos(true);
        const response = await PacienteService.getBonos(pacienteId);
        setBonos(response.data || []);
      } catch (e: any) {
          if (e.response?.status === 401) {
            localStorage.clear();
            history(constantes.R_HOME);
          } else {
            const error: ILog = {
              method: "FacturarForm.Component.cargarBonos",
              level: "error",
              message: e.message,
              meta: e.response,
            };
            LogService.logError(error);
            setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
          }
        } finally {
          setLoadingBonos(false);
        }
    };

    const filtrarBonosFactura = () => {
      return bonos.filter((bono) => {
        const esBonoActual = isEdit && bono._id === bonoIdOriginal;

        const sesionesUsadas = esBonoActual
          ? Math.max(0, bono.usado - 1)
          : bono.usado;

        const sesionesRestantes = bono.cantidad - sesionesUsadas;
        const tieneSesiones = sesionesRestantes > 0;
  
        const noCaducado =
          !bono.caducidad ||
          moment(bono.caducidad).startOf('day').isSameOrAfter(moment().startOf('day'));

        // Al editar/rectificar, la entidad de la factura ya está fijada — solo se pueden
        // elegir bonos de esa misma entidad (salvo el que ya estaba usando, por si acaso).
        const mismaEntidad =
          facturaEntidadId === undefined ||
          (bono.entityId ?? null) === (facturaEntidadId ?? null);

        // Un bono de una entidad desactivada no se puede usar hasta activarla: ni se ofrece.
        const entidadActiva = ConfigUtils.isEntidadActiva(bono.entityId);

        return esBonoActual || (tieneSesiones && noCaducado && mismaEntidad && entidadActiva);
      });
    };

    const handleChangeServicio = (event: SelectChangeEvent) => {
      const serv = servicios.find((s) => s.internalID?.toString() === event.target.value);
      if (serv) {
        setSelectedServicio(serv);
        setFacturable(serv.facturable ?? true);
        setServicio(serv.nombre ?? "");
        setConcepto(serv.concepto ?? "");
        setPrecio(serv.precio ?? 0);
        setImpuesto(serv.impuesto ?? 0);
        const precioTotalCalculado = serv.precio + (serv.precio * serv.impuesto) / 100;
        setPrecioTotal(precioTotalCalculado);
        setInputPrecioBase(serv.precio.toFixed(2).replace(".", ","));
        setFacturaPagada(false);
        setMetodoPago(undefined);
        setErrors({});
      }
    };

    const inputPrecioBaseMostrar = bonoSeleccionado
      ? "0,00"
      : inputPrecioBase;

    const impuestoMostrar = bonoSeleccionado
      ? 0
      : Number(impuesto);

    const precioTotalMostrar = bonoSeleccionado
      ? 0
      : Number(precioTotal);

    return (
        <>
            {isEdit &&
              <Box>
                <Box display="flex" flexWrap="wrap" gap={4} alignItems="center" sx={{ mb: 2 }}>
                  {/* Número */}
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Nº:</Typography>
                    {<Typography variant="body2">{formatNumeroFactura(numeroFactura)}</Typography>}
                  </Box>

                  {/* Fecha */}
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Fecha factura:</Typography>
                    <Typography variant="body2">
                      {new Date(fechaFactura).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            }

            <Grid container spacing={2}>
              {/* Tipo de factura */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.tipoPago}>
                  <InputLabel id="select-tipo-label">Tipo de factura*</InputLabel>
                  <Select
                    disabled={
                      tieneComplementaria ||
                      isRectificar ||
                      esRectificativa ||
                      (bonoId !== "" && isEdit)
                    }
                    labelId="select-tipo-label"
                    id="select-tipo"
                    value={tipoPago}
                    label="Tipo de factura*"
                    onChange={(e) => {
                      setErrors((prev: IFacturaErrors) => ({ ...prev, tipoPago: "" }));
                      setTipoPago(e.target.value as any);
                    }}
                  >
                    <MenuItem value="total">Total</MenuItem>
                    <MenuItem value="parcial">Parcial</MenuItem>
                  </Select>
                  
                  {errors.tipoPago && (
                    <Typography color="error" variant="caption">
                      {errors.tipoPago}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
                
              {/* Servicio */}
              {!isEdit && !isRectificar && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={!!errors.servicio}>
                    <InputLabel id="select-servicio-label">Servicio*</InputLabel>
                    <Select
                      labelId="select-servicio-label"
                      value={selectedServicio?.internalID?.toString() || ""}
                      label="Servicio*"
                      onChange={handleChangeServicio}
                    >
                      {servicios
                        .filter((m) => m.activo && !m.recurrente)
                        .map((m) => (
                          <MenuItem key={m.internalID?.toString()} value={m.internalID?.toString()}>
                            {m.nombre}
                          </MenuItem>
                        ))}
                    </Select>
                      
                    {errors.servicio && (
                      <Typography sx={{ color: "#d32f2f", fontSize: "0.75rem", mt: 1 }}>
                        {errors.servicio}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
              )}
            </Grid>

            {(selectedServicio || (isEdit || isRectificar)) && (
              <>
                {/* Facturable switch */}
                <FormControlLabel
                  control={
                    <Switch
                      checked={facturable}
                      onChange={(e) => {
                        if (metodoPago) {
                          setAlert({ open: true, severity: "warning", msg: "No se puede marcar como no facturable: ya está pagada" })
                          return;
                        }
                        const value = e.target.checked;
                        if (!value && facturable) {
                          setConfirmNoFacturable(true);
                          return;
                        }
                        setFacturable(value);
                      }}
                      disabled={(isEdit && tieneComplementaria) || isRectificar || esRectificativa || (bonoId !== "" && isEdit)}
                    />
                  }
                  label="Facturable"
                  sx={{ mb: 1 }}
                />

                {/* Si no es facturable, ocultamos los demás campos */}
                {facturable && (
                  <>
                    <Grid container spacing={{ xs: 0, md: 2 }}>
                      {/* Servicio */}
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="Servicio*"
                          fullWidth
                          sx={{ mb: 2 }}
                          value={servicio}
                          onChange={(e) => {
                            setServicio(e.target.value);
                            setErrors((prev: IFacturaErrors) => ({ ...prev, general: undefined }));
                            setErrors((prev: IFacturaErrors) => ({ ...prev, servicioName: undefined }));
                          }}
                          error={!!errors.servicioName}
                          helperText={errors.servicioName}
                          disabled={(bonoId !== "" && (isEdit || isRectificar))}
                        />
                      </Grid>
                      {/* Concepto */}
                      <Grid item xs={12} md={8}>
                        <TextField
                          label="Concepto*"
                          fullWidth
                          sx={{ mb: 2 }}
                          value={concepto}
                          onChange={(e) => {
                            setConcepto(e.target.value);
                            setErrors((prev: IFacturaErrors) => ({ ...prev, general: undefined }));
                            setErrors((prev: IFacturaErrors) => ({ ...prev, concepto: undefined }));
                          }}
                          error={!!errors.concepto}
                          helperText={errors.concepto}
                        />
                      </Grid>
                    </Grid>

                    <Grid container spacing={{ xs: 0, md: 2 }}>
                      {/* Precio */}
                      <Grid item xs={12} md={4}>
                      <TextField
                        label={`Precio base (${simboloMoneda})*`}
                        fullWidth
                        type="text"
                        sx={{ mb: 2 }}
                        value={inputPrecioBaseMostrar}
                        onChange={(e) => {
                          let raw = e.target.value;
                          setInputPrecioBase(raw);

                          const normalized = raw.replace(",", ".");
                          if (/^\d*([.,]\d*)?$/.test(raw)) {
                            setErrors((prev: IFacturaErrors) => ({ ...prev, general: undefined }));
                            const nuevoPrecio = parseFloat(normalized);
                          
                            if (!isNaN(nuevoPrecio)) {
                              setPrecio(nuevoPrecio);
                              setPrecioTotal(nuevoPrecio + (nuevoPrecio * impuesto) / 100);
                            } else {
                              setPrecio(0);
                              setPrecioTotal(0);
                            }
                          }
                        }}
                        onBlur={() => {
                          const formatted = precio.toFixed(2).replace(".", ",");
                          setInputPrecioBase(formatted);
                        }}
                        error={!!errors.precio}
                        helperText={errors.precio}
                      />
                      </Grid>

                      {/* Impuesto */}
                      <Grid item xs={12} md={4}>
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel id="select-impuesto-label">Impuesto (%)*</InputLabel>
                        <Select
                          labelId="select-impuesto-label"
                          value={impuestoMostrar}
                          label="Impuesto (%)*"
                          onChange={(e) => {
                            const nuevoImp = Number(e.target.value);
                            setImpuesto(nuevoImp);
                            setPrecioTotal(precio + (precio * nuevoImp) / 100);
                            setErrors((prev: IFacturaErrors) => ({ ...prev, general: undefined }));
                          }}
                        >
                          {constantes.IMPUESTOS.map((imp) => (
                            <MenuItem key={imp} value={imp}>
                              {imp}%
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      </Grid>

                      <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label={`Precio total (${simboloMoneda})`}
                        value={precioTotalMostrar.toFixed(2).replace(".", ",")}
                        InputProps={{ readOnly: true, sx: {
                          backgroundColor: '#f0f0f0',
                          color: '#555',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#bdbdbd'
                          }
                        }}}
                      />
                      </Grid>
                    </Grid>

                    {/* Bloque de Pago */}
                    {loadingBonos ? <CircularProgress style={{ margin: "20px auto" }} /> : 
                    <Box
                      display="flex"
                      flexWrap="wrap"
                      alignItems="center"
                    >
                      {/* Texto explicativo */}
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ flexBasis: "100%", flexGrow: 1 }}
                      >
                        * Si la factura se marca como pagada, el método de pago es obligatorio.
                      </Typography>
                                    
                      {/* Switch de factura pagada */}
                      <FormControlLabel
                        control={
                          <Switch
                            checked={facturaPagada}
                            onChange={(e) => {
                              const pagada = e.target.checked;
                                                        
                              setFacturaPagada(pagada);
                              setMetodoPago(undefined);
                                                        
                              if (!pagada) {
                                setBonoSeleccionado(null);
                                setBonoId("");
                                setSelectedMetodo("");
                              }
                            
                              setErrors((prev: IFacturaErrors) => ({
                                ...prev,
                                metodoPago: undefined,
                              }));
                            }}
                          />
                        }
                        label="Factura pagada"
                        sx={{ m: 0, mb: facturaPagada ? 0 : { xs: 0, md: 7 } }}
                      />
                    
                      {/* Select de método de pago */}
                      {facturaPagada && (
                        <Grid container spacing={2} alignItems="flex-start">
                          {/* Método de pago */}
                          <Grid item xs={12} sm={6} md={6}>
                            <FormControl fullWidth error={!!errors.metodoPago}>
                              <InputLabel id="metodo-pago-label">Método de pago*</InputLabel>
                              <Select
                                labelId="metodo-pago-label"
                                value={
                                  selectedMetodo &&
                                  [
                                    ...metodosPago.map(m => m.nombre),
                                    ...filtrarBonosFactura().map(b => b._id),
                                  ].includes(selectedMetodo)
                                    ? selectedMetodo
                                    : ""
                                }
                                label="Método de pago*"
                                onChange={(e) => {
                                  const value = e.target.value;
                                  const metodo = metodosPago.find((m) => m.nombre === value);
                                  if (metodo) {
                                    setBonoSeleccionado(null);
                                    setBonoId("");
                                    setMetodoPago({
                                      nombre: metodo.nombre,
                                      codigo: metodo.codigo,
                                      fechaPago: metodoPago?.fechaPago || moment(),
                                    });
                                    setSelectedMetodo(metodo.nombre);
                                  } else {
                                    const bono = filtrarBonosFactura().find(b => b._id === value);
                                    setBonoId(bono?._id || "");
                                    setBonoSeleccionado(bono || null);
                                    setMetodoPago({
                                      nombre: bono?.nombre || "",
                                      codigo: constantes.MetodoPagoCodigo.Otro,
                                      fechaPago: moment(),
                                    });
                                    setSelectedMetodo(bono?._id || "");
                                  }
                                  setErrors((prev: IFacturaErrors) => ({ ...prev, general: undefined }));
                                  setErrors((prev: IFacturaErrors) => ({ ...prev, metodoPago: undefined }));
                                }}
                              >
                                {filtrarBonosFactura().map((b) => {
                                  const esBonoActual = isEdit && b._id === bonoIdOriginal;
                                  const usadas = esBonoActual
                                    ? Math.max(0, b.usado - 1)
                                    : b.usado;

                                  const restantes = b.cantidad - usadas;
                                  const nombresServicios = b.servicios
                                    .map(id =>
                                      servicios.find(s => parseInt(s.internalID!) === id)?.nombre
                                    )
                                    .filter(Boolean);
                                  const nombreServicio = servicio;
                                  const servicioIncluido = nombresServicios.includes(nombreServicio);
                                  const validoParaFactura = tipoPago === "total" && servicioIncluido;
                                  const disabled = !validoParaFactura && !esBonoActual;     
                                  return (
                                    <MenuItem key={b._id} value={b._id} disabled={disabled}>
                                      {b.nombre} ({restantes}/{b.cantidad})
                                      {!servicioIncluido && " — No válido para este servicio"}
                                      {tipoPago !== "total"  && " — No válido para esta factura"}
                                    </MenuItem>
                                  );
                                })}
                                {metodosPago.filter((m) => m.activo).map((m) => (
                                  <MenuItem key={m.codigo} value={m.nombre}>
                                    {m.nombre}
                                  </MenuItem>
                                ))}
                              </Select>
                              {errors.metodoPago && (
                                <Typography color="error" variant="caption">
                                  {errors.metodoPago}
                                </Typography>
                              )}
                            </FormControl>
                          </Grid>
                            
                          {/* Fecha de pago */}
                          <Grid item xs={12} sm={6} md={6}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                              <DatePicker
                                label="Fecha de pago"
                                value={metodoPago?.fechaPago}
                                onChange={(date) => {
                                  if (metodoPago) {
                                    setMetodoPago({ ...metodoPago, fechaPago: date ?? moment() });
                                    setErrors((prev: IFacturaErrors) => ({ ...prev, general: undefined }));
                                    setErrors((prev: IFacturaErrors) => ({ ...prev, fechaPago: undefined }));
                                  }
                                }}
                                renderInput={(params) => ( <TextField {...params} required fullWidth error={!!errors.fechaPago} helperText={errors.fechaPago} /> )}
                              />
                            </LocalizationProvider>
                          </Grid>
                        </Grid>
                      )}

                      {errors.general && <Alert sx={{ mt: 1 }} severity="warning">{errors.general}</Alert>}
                    </Box>
                    }
                  </>
                )}

                {!facturable && (
                  <TextField
                    label="Motivo no facturable"
                    fullWidth
                    multiline
                    minRows={2}
                    sx={{ mb: 2 }}
                    InputLabelProps={{ shrink: true }}
                    value={motivoNoFacturable}
                    onChange={(e) => {
                      setMotivoNoFacturable(e.target.value);
                    }}
                  />  
                )}
              </>
            )}
            </>
    );
};

export default FacturarForm;